import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useOrganizationStore } from '@/store/pinia/organization'
import { ObjWithPrivateProps, RepoUrl } from '@/utils/classes'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

export const state = () => ({
  detail: null,
  branches: null,
  errors: {
    branches: [],
    configRepository: [],
  },
})

export const useConfigRepositoryStore = defineStore('configRepository', {
  state,
  getters: {
    configRepository: (state) => state.detail,
    hasBranches: (state) => !_.isEmpty(state.branches),
    hasBranchesError: (state) => !_.isEmpty(state.errors.branches),
  },
  actions: {
    /**
     * Clear the current errors list.
     */
    CLEAR_CR_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_CR_STATE: makeResetStoreAction(state),

    /**
     * Replace the current errors array.
     */
    SET_ERRORS: makeSetErrorsAction(),

    /**
     * Set branches in state.
     * @param {Array} branches
     * @returns {void}
     */
    SET_BRANCHES (branches) {
      this.branches = branches
    },

    /**
     * Replace the current config repository object in state.
     * @param {Object} configRepository
     * @returns {void}
     */
    SET_CONFIG_REPOSITORY (configRepository) {
      this.detail = new ObjWithPrivateProps(configRepository, {
        _repoURL: new RepoUrl(configRepository),
      })
    },

    /**
     * Create a new config repository.
     * @param {Object} params
     * @param {Object} params.configRepository
     * @param {VueRouter} [params.$router]
     * @returns {Promise<void>}
     */
    async CREATE_CONFIG_REPOSITORY ({ configRepository, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_CR_ERRORS('configRepository')

      const { data, errors } = (await this.$cycloid.ydAPI.createConfigRepository(
        orgStore.orgCanonical,
        configRepository,
      )) || {}

      if (data) {
        this.SET_CONFIG_REPOSITORY(_.merge(data, configRepository))
        const content = i18n.global.t('alerts.success.configRepository.created', { configRepoName: data.name })
        alertStore.SHOW_ALERT({ type: 'success', content })
        if ($router) $router.replace({ name: 'configRepository', params: { configRepositoryCanonical: configRepository.canonical } })
      }

      if (errors) this.SET_ERRORS({ key: 'configRepository', errors })
    },

    /**
     * Delete a config repository.
     * @param {Object} params
     * @param {Object} params.configRepository
     * @param {VueRouter} [params.$router]
     * @returns {Promise<void>}
     */
    async DELETE_CONFIG_REPOSITORY ({ configRepository, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_CR_ERRORS('configRepository')

      const { errors } = (await this.$cycloid.ydAPI.deleteConfigRepository(
        orgStore.orgCanonical,
        configRepository.canonical,
      )) || {}

      if (errors) {
        this.SET_ERRORS({ key: 'configRepository', errors })
      } else {
        this.RESET_CR_STATE()
        const content = i18n.global.t('alerts.success.configRepository.deleted', { configRepoName: configRepository.name })
        alertStore.SHOW_ALERT({ type: 'success', content })
        if ($router) $router.replace({ name: 'configRepositories' })
      }
    },

    /**
     * Get repository branches.
     * @param {Object} params
     * @param {string} params.credentialCanonical
     * @param {string} params.url
     * @returns {Promise<void>}
     */
    async GET_BRANCHES ({ credentialCanonical, url }) {
      const orgStore = useOrganizationStore()
      const code = 'InvalidConfigRepositoryNotFound'

      const { data, errors } = (await this.$cycloid.ydAPI.getRepoBranches(
        orgStore.orgCanonical,
        credentialCanonical,
        url,
      )) || {}

      if (data) {
        this.CLEAR_CR_ERRORS('configRepository')
        this.CLEAR_CR_ERRORS('branches')
        this.SET_BRANCHES(data.sort())
      }

      if (_.every([data, errors], _.isEmpty)) {
        this.SET_ERRORS({ key: 'configRepository', errors: [{ code, message: i18n.global.t(`apiErrors.${code}`) }] })
      }

      if (errors) this.SET_ERRORS({ key: 'branches', errors })
    },

    /**
     * Get config repository by canonical.
     * @param {Object} params
     * @param {string} params.configRepositoryCanonical
     * @returns {Promise<void>}
     */
    async GET_CONFIG_REPOSITORY ({ configRepositoryCanonical }) {
      const orgStore = useOrganizationStore()

      this.CLEAR_CR_ERRORS('configRepository')

      const { data, errors } = (await this.$cycloid.ydAPI.getConfigRepository(
        orgStore.orgCanonical,
        configRepositoryCanonical,
      )) || {}

      if (data) this.SET_CONFIG_REPOSITORY(data)
      if (errors) this.SET_ERRORS({ key: 'configRepository', errors })
    },

    /**
     * Update an existing config repository.
     * @param {Object} params
     * @param {Object} params.configRepository
     * @returns {Promise<void>}
     */
    async UPDATE_CONFIG_REPOSITORY ({ configRepository }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_CR_ERRORS('configRepository')

      const { data, errors } = (await this.$cycloid.ydAPI.updateConfigRepository(
        orgStore.orgCanonical,
        configRepository,
      )) || {}

      if (data) {
        this.SET_CONFIG_REPOSITORY(_.merge(data, configRepository))
        const content = i18n.global.t('alerts.success.configRepository.updated', { configRepoName: configRepository.name })
        alertStore.SHOW_ALERT({ type: 'success', content })
      }

      if (errors) this.SET_ERRORS({ key: 'configRepository', errors })
    },
  },
})
